// /(app)/(store)/customer/updateCustomer/index.tsx
import { createFileRoute } from "@tanstack/react-router";
import { useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useState, useEffect } from "react";
import { currentUserSelector, ProfileTypeEnum } from "@/features/auth";
import {
  CustomerForm,
  CustomerValidationSchema,
} from "@/features/customer/validation";
import { BusinessTypeEnum } from "@/enum/businessType.enum";
import { useGetCustomerQuery, useUpdateClientMutation } from "@/features/customer/api";
import { pick } from "@/utils/common";
import { SellerBuyerRootMapping } from "@/interfaces";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { removeEmptyValues } from "@/utils";

const initialAddress = {
  line1: "",
  tehsil: "",
  locality: "",
  landmark: "",
  district: "",
  state: "",
  country: "India",
  postalCode: "",
};

const initialValue: CustomerForm = {
  name: "",
  phoneNumber: "",
  gstNumber: "",
  panNumber: "",
  clientType: "Buyer",
  businessType: BusinessTypeEnum.Individual,
  address: initialAddress,
};

function getDefaultValue(
  currentUserId?: string,
  value?: Partial<SellerBuyerRootMapping>
): CustomerForm {
  if (!value) return initialValue;
  const cleanPhoneNumber = (phone?: string) => {
    if (!phone) return "";
    return phone.replace(/^\(\d+\)\s*/, "");
  };
  const isBuyerContext = currentUserId === value.sellerStoreId;
  const context = isBuyerContext ? "buyer" : "seller";
  const baseData = {
    clientType: isBuyerContext ? "Buyer" : "Seller",
    businessType:
      value[`${context}Type`] === ProfileTypeEnum.Store
        ? BusinessTypeEnum.Business
        : BusinessTypeEnum.Individual,
  };
  const contextSpecificFields = {
    name: value[`${context}Name`] as string,
    phoneNumber: cleanPhoneNumber(
      value[`${context}PhoneNumber`] || value.buyerPhoneNumber
    ),
    gstNumber: value[`${context}GstNumber`],
    panNumber: value[`${context}PanNumber`],
    address: value[`${context}Address`] || initialAddress,
  };
  return {
    ...baseData,
    ...contextSpecificFields,
  } as unknown as CustomerForm;
}

export const Route = createFileRoute("/(app)/(store)/customer/update-customer/$sellerBuyerRouteMappingId")(
  {
    component: RouteComponent,
  }
);

function RouteComponent() {
  const { sellerBuyerRouteMappingId } = Route.useParams()
  const { data, isFetching } = useGetCustomerQuery(sellerBuyerRouteMappingId)
  const currentUser = useSelector(currentUserSelector);
  const [updateCustomer, { isLoading }] = useUpdateClientMutation();
  const [isLocalCustomer, setIsLocalCustomer] = useState(false);
  const [isUpdated, setIsUpdated] = useState(false);

  const {
    control,
    reset,
    handleSubmit,
    setValue,
    watch,
    formState: { isSubmitting, isDirty },
  } = useForm<CustomerForm>({
    resolver: zodResolver(CustomerValidationSchema),
    mode: 'onBlur',
    defaultValues: initialValue,
  })

  const individualCustomer = watch('businessType') !== BusinessTypeEnum.Individual

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      setIsUpdated(true)
    })
    return () => subscription.unsubscribe()
  }, [watch, setValue])

  async function handleUpdateCustomer(data: CustomerForm) {
    try {
      const payload = removeEmptyValues(data)
      delete payload.phoneNumber
      const response = await updateCustomer({ clientId: sellerBuyerRouteMappingId, ...payload }).unwrap()
      if (response.data) {
        toast.success("Customer created Successfully.")
        handleResetForm()
      }
    } catch (error: any) {
      toast.error(error?.data?.message ?? "Something went wrong")
    }
  }

  function handleResetForm() {
    reset(getDefaultValue(currentUser?._id, data))
    setIsLocalCustomer(false)
  }

  function handleOnChange(value: boolean) {
    if (value && currentUser?.profileType === 'Store') {
      setValue(
        'address',
        pick(currentUser.address, [
          'country',
          'district',
          'postalCode',
          'state',
          'tehsil',
        ])
      )
    } else {
      setValue('address', initialAddress)
    }
    setIsLocalCustomer(value)
  }
  useEffect(() => {
    if (data) {
      reset(getDefaultValue(currentUser?._id, data))
    }
  }, [data])
  useEffect(() => {
    const subscription = watch(() => {
      setIsUpdated(true);
    });
    return () => subscription.unsubscribe();
  }, [watch, setValue]);
  return (
    <div className="screenContainer">
      <div className="container">
        <div className="formGroup" style={{ maxWidth: "50%" }}>
          <Controller
            control={control}
            name="businessType"
            render={({ field: { onChange, value } }) => (
              <div className="toggleContainer">
                <label className="toggleLabel">Customer Type</label>
                <div className="toggleButtons">
                  {Object.values(BusinessTypeEnum).map((option) => (
                    <button
                      key={option}
                      type="button"
                      className={`toggleButton ${value === option ? 'active' : ''}`}
                      onClick={() => onChange(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>
            )}
          />
        </div>
        <div className='rowContainer'>
          <div className="formGroup">
            <Controller
              control={control}
              name="name"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">
                    Full Name
                  </label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter full name"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>

          <div className="formGroup">
            <Controller
              control={control}
              name="phoneNumber"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">
                    Phone Number <span className="required">*</span>
                  </label>
                  <input
                    {...field}
                    type="tel"
                    placeholder="Enter phone number"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>
        </div>

        {individualCustomer && (
          <div className="businessDetails rowContainer">
            <div className="formGroup">
              <Controller
                control={control}
                name="gstNumber"
                render={({ field, fieldState: { error } }) => (
                  <div className="inputGroup">
                    <label className="inputLabel">GST Number</label>
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter GST number"
                      className={`formInput ${error ? 'error' : ''}`}
                    />
                    {error && <span className="errorText">{error.message}</span>}
                  </div>
                )}
              />
            </div>

            <div className="formGroup">
              <Controller
                control={control}
                name="panNumber"
                render={({ field, fieldState: { error } }) => (
                  <div className="inputGroup">
                    <label className="inputLabel">PAN Number</label>
                    <input
                      {...field}
                      type="text"
                      placeholder="Enter PAN number"
                      className={`formInput ${error ? 'error' : ''}`}
                    />
                    {error && <span className="errorText">{error.message}</span>}
                  </div>
                )}
              />
            </div>
          </div>
        )}
         <h1>Address</h1>
        <div className='rowContainer'>

          <div className="formGroup">
            <Controller
              control={control}
              name="address.line1"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">Line 1</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="House number or street"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>
          <div className="formGroup">
            <div className="localCustomerToggle">
              <span className="toggleLabel">Local Customer</span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={isLocalCustomer}
                  onChange={(e) => handleOnChange(e.target.checked)}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div className="addressGrid">
          <div className="formGroup">
            <Controller
              control={control}
              name="address.landmark"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">Landmark</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter landmark"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>

          <div className="formGroup">
            <Controller
              control={control}
              name="address.locality"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">Locality</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter Locality"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>

          <div className="formGroup">
            <Controller
              control={control}
              name="address.tehsil"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">Tehsil</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter city"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>

          <div className="formGroup">
            <Controller
              control={control}
              name="address.district"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">District</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter district"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>

          <div className="formGroup">
            <Controller
              control={control}
              name="address.state"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">State</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter state"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>

          <div className="formGroup">
            <Controller
              control={control}
              name="address.country"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">Country</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter country"
                    defaultValue="India"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>

          <div className="formGroup">
            <Controller
              control={control}
              name="address.postalCode"
              render={({ field, fieldState: { error } }) => (
                <div className="inputGroup">
                  <label className="inputLabel">Postal Code</label>
                  <input
                    {...field}
                    type="text"
                    placeholder="Enter postal code"
                    className={`formInput ${error ? 'error' : ''}`}
                  />
                  {error && <span className="errorText">{error.message}</span>}
                </div>
              )}
            />
          </div>
        </div>
        <div className="footer">
          <Button type='reset' variant="outline" size="xl" disabled={!isUpdated || isLoading || isSubmitting || !isDirty} onClick={handleResetForm}>
            RESET
          </Button>
          <Button type='reset' size='xl' disabled={isLoading || isSubmitting || !isDirty}
            onClick={handleSubmit(handleUpdateCustomer)}>
            UPDATE
          </Button>
        </div>
      </div>

    </div>
  )
}

